import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../scripts/AuthContext";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import AuthShell from "@/components/brand/AuthShell";
import { API_BASE_URL } from "@/lib/apiBase";
import { safeInviteReturn } from "@/lib/inviteReturn";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = String(searchParams.get("token") || "").trim();
  const returnTo = safeInviteReturn(searchParams.get("return"));
  const { login } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [status, setStatus] = useState(token ? "verifying" : "missing");

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/verify-email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ token }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const message =
            (typeof data?.message === "string" && data.message) ||
            (Array.isArray(data?.message) && data.message[0]) ||
            "Verification failed.";
          throw new Error(message);
        }
        if (cancelled) return;
        if (data?.access_token) {
          login(data.access_token);
          setStatus("ok");
          navigate(returnTo || "/dashboard", { replace: true });
          return;
        }
        throw new Error("Verification failed.");
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Verification failed.");
          setStatus("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, login, navigate, returnTo]);

  return (
    <AuthShell
      title="Verify your email"
      subtitle="Confirming your Circa account."
      footer={
        <p>
          <Link to="/signup" className="font-medium text-primary hover:underline">
            Sign up again
          </Link>
          {" · "}
          <Link to="/login" className="font-medium text-primary hover:underline">
            Sign in
          </Link>
        </p>
      }
    >
      {status === "verifying" ? (
        <p className="text-sm text-muted-foreground">Verifying…</p>
      ) : null}
      {status === "missing" ? (
        <Alert variant="destructive">
          <AlertDescription>
            This verification link is missing a token. Open the link from your
            email, or sign up again.
          </AlertDescription>
        </Alert>
      ) : null}
      {status === "error" ? (
        <div className="space-y-4">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button asChild variant="outline" className="w-full">
            <Link to="/signup">Sign up again</Link>
          </Button>
        </div>
      ) : null}
    </AuthShell>
  );
};

export default VerifyEmail;
